import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * DSSnackbar — 화면 하단 중앙에 잠깐 떴다 사라지는 알림 (figma 5404:199074)
 * 흰 카드 + 메시지 + 밑줄 '닫기'. duration 후 자동 닫힘.
 *
 * 단독 사용:
 *   <DSSnackbar message={msg} onClose={() => setMsg(null)} />
 *
 * 훅 사용(권장) — 메시지 상태·타이머를 함께 관리한다:
 *   const { snackbar, showSnackbar } = useSnackbar();
 *   showSnackbar("저장했어요.");
 *   return (<>{...}{snackbar}</>);
 *
 * body 포털이라 비포털 모달(DSModal/DSDialog) 위에도 가려지지 않는다.
 */

const DEFAULT_DURATION = 4000;

const DSSnackbar = ({
  message,
  onClose,
  duration = DEFAULT_DURATION,
  closeText = "닫기",
}) => {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!message) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (duration > 0) {
      timerRef.current = setTimeout(() => onClose && onClose(), duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [message, duration, onClose]);

  if (!message) return null;

  return createPortal(
    <div className="ds-snackbar" role="status">
      <span className="ds-snackbar__msg">{message}</span>
      {closeText ? (
        <button
          type="button"
          className="ds-snackbar__close"
          onClick={() => onClose && onClose()}
        >
          {closeText}
        </button>
      ) : null}
    </div>,
    document.body,
  );
};

/**
 * 메시지 상태 + 스낵바 엘리먼트를 한 번에 돌려준다.
 * @param {number} [duration=4000]
 * @returns {{ snackbar: React.ReactNode, showSnackbar: (message: string) => void, closeSnackbar: () => void }}
 */
export const useSnackbar = (duration = DEFAULT_DURATION) => {
  const [message, setMessage] = useState(null);
  const closeSnackbar = useCallback(() => setMessage(null), []);
  const showSnackbar = useCallback((next) => setMessage(next), []);

  return {
    snackbar: (
      <DSSnackbar
        message={message}
        onClose={closeSnackbar}
        duration={duration}
      />
    ),
    showSnackbar,
    closeSnackbar,
  };
};

export default DSSnackbar;
